import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import FeatherIcon from 'feather-icons-react';
import FeatureCard from '../../../components/intro/FeatureCard';
import {
    INTRO_CATEGORIES,
    SITE_URL,
    SITE_NAME,
    OG_IMAGE,
    APPSTORE_URL,
    TWITTER_HANDLE
} from '../../../data/introFeatures';
import './intro.scss';

const IntroCategory = ({ match }) => {
    const slug = match && match.params ? match.params.slug : '';
    const category = INTRO_CATEGORIES.find(c => c.slug === slug);

    // 잘못된 slug → 메인으로 graceful fallback
    if (!category) return <Redirect to="/intro" />;

    const canonical = SITE_URL + '/intro/' + category.slug;

    // BreadcrumbList JSON-LD
    const breadcrumbLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: '온리플', item: SITE_URL + '/intro' },
            { '@type': 'ListItem', position: 2, name: category.title, item: canonical }
        ]
    };

    // ItemList JSON-LD — 카테고리의 기능 목록 (검색엔진이 카테고리를 "기능 모음" 으로 인지)
    const itemListLd = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: category.title,
        description: category.summary,
        itemListElement: category.features.map((f, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: f.name,
            description: f.desc
        }))
    };

    return (
        <div className="intro">
            <Helmet>
                <html lang="ko" />
                <title>{category.seo.title}</title>
                <meta name="description" content={category.seo.description} />
                <meta name="keywords" content={category.seo.keywords} />
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
                <meta name="author" content="ONNOMADS" />
                <link rel="canonical" href={canonical} />

                {/* Open Graph */}
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content={SITE_NAME} />
                <meta property="og:locale" content="ko_KR" />
                <meta property="og:title" content={category.seo.title} />
                <meta property="og:description" content={category.seo.description} />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={category.seo.title} />
                <meta property="og:url" content={canonical} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content={TWITTER_HANDLE} />
                <meta name="twitter:title" content={category.seo.title} />
                <meta name="twitter:description" content={category.seo.description} />
                <meta name="twitter:image" content={OG_IMAGE} />

                {/* Structured Data */}
                <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
                <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
            </Helmet>

            {/* breadcrumb */}
            <nav className="intro-crumb" aria-label="breadcrumb">
                <div className="intro-crumb__inner">
                    <Link to="/intro">온리플</Link>
                    <FeatherIcon icon="chevron-right" size="13" />
                    <span>{category.title}</span>
                </div>
            </nav>

            {/* 카테고리 hero */}
            <section className="intro-cat-hero">
                <div className="intro-section__inner">
                    <span className="intro-cat-hero__icon">
                        <FeatherIcon icon={category.icon} size="28" />
                    </span>
                    <h1>{category.title}</h1>
                    <p>{category.summary}</p>
                </div>
            </section>

            {/* 기능 카드 grid */}
            <section className="intro-section">
                <div className="intro-section__inner">
                    <div className="intro-feature-grid">
                        {category.features.map(f => (
                            <FeatureCard key={f.id} feature={f} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="intro-cta-band">
                <div className="intro-section__inner">
                    <h2>{category.title}, 14일 동안 마음껏 만나보세요</h2>
                    <p>결제 정보 없이도 모든 기능을 활짝 열어드립니다.</p>
                    <div className="intro-cta-band__btns">
                        <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="intro-btn intro-btn--primary">
                            카페24 앱스토어에서 시작하기 <FeatherIcon icon="external-link" size="14" />
                        </a>
                        <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="intro-btn intro-btn--white">
                            무료 사용하기 <FeatherIcon icon="external-link" size="14" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IntroCategory;
